import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Redirect } from 'react-router-dom';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../graphql/iot-documents';
import { getThingOperationalStatus } from '../../services';
import { AplicacaoInfo } from '../all-variables/content';
import { Return, Thing, Overview, List } from './content';
import { Loader, OperationalStatus } from '../../components';
import { createReconnectingSubscription } from '../../utils';
import * as s from './styled-operational';

const formatRangeNumber = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return null;

  return number.toLocaleString('pt-BR', {
    maximumFractionDigits: 2,
  });
};

const getRangeAlertInfo = (alert) => {
  if (!alert || alert.source !== 'range') return null;

  const variableName =
    alert.variable?.name || alert.variable?.ihmname || 'Variável';
  const value = formatRangeNumber(alert.value);
  const threshold = formatRangeNumber(alert.threshold);
  const unit = alert.unit || '';
  const directionLabel =
    alert.direction === 'high'
      ? 'acima do máximo'
      : alert.direction === 'low'
      ? 'abaixo do mínimo'
      : 'fora do range';
  const detail =
    value && threshold
      ? `${value}${unit ? ` ${unit}` : ''} ${directionLabel} ${threshold}${
          unit ? ` ${unit}` : ''
        }`
      : directionLabel;

  return {
    variableName,
    detail,
    isAlarm: String(alert.level || '').startsWith('E'),
  };
};

const Operacional = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  // Guard de refresh: a rota nao tem :id, entao se chegamos sem state
  // (refresh direto, link copiado), nao temos como reconstruir o
  // contexto operacional. Fallback {} previne crash; Redirect apos os
  // hooks volta pra rota anterior.
  const { thing, aplicacao, variables, values, dataThingConfig, alert } =
    history.location.state || {};
  const rangeAlertInfo = getRangeAlertInfo(alert);
  // Antes: useSelector(state => state.iot) retornava o slice inteiro,
  // entao QUALQUER atualizacao de subscription (de qualquer thing do sistema)
  // disparava re-render desta tela. Agora seleciona so o thing atual.
  const iotThing = useSelector((state) =>
    thing && state.iot ? state.iot[thing.thing] : null
  );
  const operationalStatus = useSelector(
    (state) => state.aplicacao.operationalStatus
  );
  // realtimeData lido via useSelector + ref. Antes lia direto via
  // store.getState() dentro do handler da subscription, o que e
  // anti-pattern: quebra o rastreamento do Redux (Devtools nao mostra
  // a leitura) e nao reagiria a um eventual replace do store. O ref
  // mantem o valor mais recente acessivel em closure sem forcar
  // re-render adicional.
  const realtimeData = useSelector((state) => state.aplicacao.realtimeData);
  const realtimeDataRef = useRef(realtimeData);
  useEffect(() => {
    realtimeDataRef.current = realtimeData;
  }, [realtimeData]);
  const [conexoes, setConexoes] = useState([]);
  const [placePin, setPlacePin] = useState(false);
  const [pins, setPins] = useState([]);
  const [highlightVar, setHighlightVar] = useState(false);
  useEffect(() => {
    if (thing) {
      let mounted = true;
      conexoes.forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });

      const connections = [];

      const totalThings = 1;
      let currentValue = {};
      let recon = null;
      // Dedup do getIOT: antes este effect disparava DUAS requisicoes
      // getIOT identicas (uma em getData(), outra em getValues() abaixo)
      // com o mesmo thingName. Agora roda uma unica vez e reusa o
      // response pros dois setters (IOT/SET_VALUES_THING +
      // OPERACIONAL/SET_REALTIME).
      const fetchAndDispatch = async (iotQuery) => {
        const response = await API.graphql(
          graphqlOperation(iotQuery, { thingName: thing.thing })
        );
        const dataValue =
          'getIOT' in response ? response.getIOT || {} : response.data.getIOT;

        if (!values && ('getIOT' in response || 'getIOT' in response.data)) {
          dispatch({
            type: 'IOT/SAGA_GET_THING_VARIABLES',
            thingId: thing.id,
            thingName: thing.thing,
            typeId: 6,
          });

          dispatch({
            type: 'IOT/SET_VALUES_THING',
            values: dataValue,
            thingName: thing.thing,
            // unsubscribe aponta pro helper de reconnect (recon) declarado
            // abaixo neste effect. Como esse callback so e invocado depois
            // (quando algo no Redux chama .unsubscribe()), o closure
            // resolve a referencia em tempo de chamada.
            unsubscribe: () => {
              if (recon && typeof recon.unsubscribe === 'function') {
                recon.unsubscribe();
              }
            },
          });
        }

        // Mesmo response: agrega valores iniciais pro realtime do
        // operacional (replica a antiga getValues()).
        if ('getIOT' in response || (response.data && response.data.getIOT)) {
          const valueThing = {
            type: '1',
            onTime: dataValue.PotenciaAtivaMedia,
            vazao: dataValue.Vazao,
            potencia: dataValue.PotenciaAtivaMedia,
          };
          const total = { onTime: 0, vazao: 0, potencia: 0 };
          if (valueThing.type === '1') {
            total.onTime += valueThing.onTime;
          } else {
            total.vazao += valueThing.vazao;
            total.potencia += valueThing.potencia;
          }
          if (totalThings === 1) {
            dispatch({
              type: 'OPERACIONAL/SET_REALTIME',
              data: total,
            });
          }
        }
      };

      // Dispara o snapshot inicial via getIOT — uma unica request
      // alimentando IOT/SET_VALUES_THING + OPERACIONAL/SET_REALTIME
      // (antes eram duas chamadas identicas em getData() e getValues()).
      Promise.all([getIOTDocument(), onUpdateIOTDocument()]).then(
        ([iotQuery, updateIotSubscription]) => {
          if (!mounted) return;
          // Subscription com reconnect: se a conexao cair (queda de wifi,
          // servidor reiniciando, token expirando), o helper reconecta
          // automaticamente com exponential backoff.
          recon = createReconnectingSubscription({
            label: `onUpdateIOT/${thing.thing}`,
            buildSubscription: ({ next, error }) =>
              API.graphql(
                graphqlOperation(updateIotSubscription, {
                  thingName: thing.thing,
                })
              ).subscribe({ next, error }),
            onNext: (subscribeData) => {
              if (
                subscribeData.value &&
                subscribeData.value.data &&
                subscribeData.value.data.onUpdateIOT
              ) {
                if (thing.type.id === '1') {
                  sumRealTimeData([
                    {
                      key: 'onTime',
                      value:
                        subscribeData.value.data.onUpdateIOT.PotenciaAtivaMedia,
                      prevValue: currentValue.onTime,
                    },
                  ]);
                  currentValue = {
                    ...currentValue,
                    onTime:
                      subscribeData.value.data.onUpdateIOT.PotenciaAtivaMedia,
                  };
                } else {
                  sumRealTimeData([
                    {
                      key: 'potencia',
                      value:
                        subscribeData.value.data.onUpdateIOT.PotenciaAtivaMedia,
                      prevValue: currentValue.potencia,
                    },
                    {
                      key: 'vazao',
                      value: subscribeData.value.data.onUpdateIOT.Vazao,
                      prevValue: currentValue.vazao,
                    },
                  ]);
                  currentValue = {
                    ...currentValue,
                    vazao: subscribeData.value.data.onUpdateIOT.Vazao,
                    potencia:
                      subscribeData.value.data.onUpdateIOT.PotenciaAtivaMedia,
                  };
                }
              }
            },
          });
          connections.push(() => recon.unsubscribe());
          fetchAndDispatch(iotQuery);
        }
      );
      setConexoes(connections);

      // Fecha as subscriptions abertas neste effect ao desmontar a tela.
      // Sem isso, navegar para fora e voltar acumula sockets WebSocket vivos.
      return () => {
        mounted = false;
        connections.forEach((unsubscribe) => {
          if (typeof unsubscribe === 'function') unsubscribe();
        });
        if (recon && typeof recon.unsubscribe === 'function') {
          recon.unsubscribe();
        }
      };
    }
    return undefined;
    // Só dispara quando o state de navegação muda; demais deps são lidas em
    // closure (intencional para evitar reabrir subscriptions).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.state]);

  useEffect(() => {
    dispatch({
      type: 'OPERACIONAL/ABRE_CONEXAO_THING',
      thing,
    });
    // Mount-only: abre a conexão da thing ao montar a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sumRealTimeData = (campos) => {
    const realtimeRedux = realtimeDataRef.current;
    let total = realtimeRedux;
    campos.forEach(({ key, value, prevValue }) => {
      total = {
        ...total,
        [key]: realtimeRedux[key] - (prevValue || 0) + value,
      };
    });
    dispatch({
      type: 'OPERACIONAL/SET_REALTIME',
      data: total,
    });
  };

  const getOperationalStatus = useCallback(
    (thingId) => {
      getThingOperationalStatus(thingId).then(({ data }) => {
        dispatch({
          type: 'OPERACIONAL/SET_OPERATIONAL_STATUS',
          data,
        });
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!thing || !thing.id) return;
    getOperationalStatus(thing.id);
  }, [thing, variables, values, getOperationalStatus]);

  // Apos todos os hooks: sem state de navegacao, redirecionamos pra
  // rota pai em vez de crashar tentando ler thing.id no JSX.
  if (!history.location.state || !thing) {
    return <Redirect to="/dashboard/customer/plant" />;
  }

  if (!values && !iotThing)
    return (
      <s.Container>
        <Loader />
      </s.Container>
    );

  return (
    <>
      {placePin && <s.Overlay onClick={() => setPlacePin(false)} />}
      <s.Container>
        {/* Topbar 1 row 3-col separado por hairline (design Operacional.html):
            crumbs (auto) | machine-card (1fr) | status-block (auto) */}
        <s.HeaderBar>
          <s.HeaderSlotLeft>
            <Return />
          </s.HeaderSlotLeft>

          <s.HeaderSlotCenter>
            <AplicacaoInfo
              nome={aplicacao && aplicacao.description}
              descricao={
                (aplicacao && (aplicacao.description || aplicacao.name)) || ''
              }
              energia={
                values || dataThingConfig?.[0] || (iotThing && iotThing.values)
              }
              type={thing.type && thing.type.type}
            />
            {rangeAlertInfo && (
              <s.RangeAlertBanner $alarm={rangeAlertInfo.isAlarm}>
                <s.RangeAlertKicker>Fora do range</s.RangeAlertKicker>
                <s.RangeAlertText title={rangeAlertInfo.detail}>
                  <strong>{rangeAlertInfo.variableName}</strong>
                  <span>{rangeAlertInfo.detail}</span>
                </s.RangeAlertText>
              </s.RangeAlertBanner>
            )}
          </s.HeaderSlotCenter>

          <s.HeaderSlotRight>
            <s.StatusLabel>Status</s.StatusLabel>
            <s.StatusCard>
              <OperationalStatus
                operationalStatus={
                  operationalStatus.length !== 0
                    ? operationalStatus
                    : aplicacao.operationalStatus
                }
              />
            </s.StatusCard>
          </s.HeaderSlotRight>
        </s.HeaderBar>

        {/* Body 2-col: left (scene-head + scene + resumo) | right (rail).
            Hairline vertical entre as colunas. */}
        <s.NewContainerBody>
          <s.LeftColumnGrid>
            {/* Scene head removido — nome da maquina ja aparece no header
                da pagina ("USINA — ...") e no breadcrumb (chip Máquina),
                entao o titulo aqui era redundante. */}

            {/* Scene — frame aspect 16/8 com o diagrama (Thing) */}
            <s.SceneSection>
              <s.SceneFrame>
                <Thing
                  thingId={thing.id}
                  name={thing.thing}
                  values={
                    values ||
                    dataThingConfig?.[0] ||
                    (iotThing && iotThing.values)
                  }
                  placePin={placePin}
                  setPlacePin={setPlacePin}
                  pins={pins}
                  setPins={setPins}
                  highlightVar={highlightVar}
                  setHighlightVar={setHighlightVar}
                  image={aplicacao?.images?.[0]}
                />
              </s.SceneFrame>
            </s.SceneSection>

            {/* Resumo — chip cyan + KPI grid 4-col (Ontime/Histórico/Dia/Última) */}
            <s.ResumoSection>
              <s.ResumoChip>Resumo</s.ResumoChip>
              <Overview
                values={
                  values ||
                  dataThingConfig?.[0] ||
                  (iotThing && iotThing.values)
                }
                thing={thing}
                type={thing.type && thing.type.type}
              />
            </s.ResumoSection>
          </s.LeftColumnGrid>

          {/* Right rail — Variáveis e comandos (List component) */}
          <s.RightColumnGrid>
            <List
              pins={pins}
              variables={variables || (iotThing && iotThing.variables)}
              values={
                values || dataThingConfig?.[0] || (iotThing && iotThing.values)
              }
              setPlacePin={setPlacePin}
              setHighlightVar={setHighlightVar}
              highlightVar={highlightVar}
              thing={thing}
            />
          </s.RightColumnGrid>
        </s.NewContainerBody>
      </s.Container>
    </>
  );
};

export default Operacional;
